<template>
  <ion-card color="light" class="no-shadow pa-xs filter-card ion-no-margin bg-white">
    <div class="content w-100">
      <div class="d-flex w-100 justify-space-between align-center">
        <p class="font-md grey bold-medium">{{ t('filters.accommodations') }}</p>
        <ion-button
          id="open-modal"
          fill="outline"
          expand="block"
          size="default"
          color="primary"
          class="text-weight-bold ion-no-margin"
        >
          <ion-icon slot="start" :icon="settingsOutline"></ion-icon>
          {{ t('filters.filters') }}</ion-button
        >
      </div>

      <ion-row class="mt-sm">
        <ion-col v-if="user?.userType !== 'relocated'" class="bg-white ion-no-padding" size="12">
          <ion-searchbar
            class="custom"
            size="small"
            :debounce="400"
            :placeholder="t('filters.location')"
            v-model="searchBar"
            @update:model-value="handleInput($event)"
          >
          </ion-searchbar>

          <ion-card
            v-if="results.length > 0 && (filters.city === '' || filters.country === '')"
            class="bg-white ion-padding no-margin-top results-card results"
          >
            <p
              @click="setFilterLocation(result)"
              v-for="(result, idx) in results"
              :key="`location-${idx}`"
            >
              {{ result.fullAddress }}
            </p>
          </ion-card>
        </ion-col>

        <ion-col v-if="user?.userType !== 'relocated'" size="6" class="ion-no-padding mt-sm">
          <VueDatePicker
            v-model="filters.startAvailabilityDate"
            @update:model-value="onUpdated()"
            auto-apply
            :clearable="false"
            :placeholder="t('search.arrival')"
            model-type="yyyy-MM-dd"
            :min-date="dayjs().add(1, 'day').format('YYYY-MM-DD')"
            locale="es"
            :enable-time-picker="false"
            :format="formatDate"
            position="center"
            :teleport="true"
            class="pr-sm"
          >
            <template #input-icon> </template>
          </VueDatePicker>
        </ion-col>
        <ion-col v-if="user?.userType !== 'relocated'" size="6" class="ion-no-padding mt-sm">
          <VueDatePicker
            :disabled="filters.startAvailabilityDate === ''"
            v-model="filters.endAvailabilityDate"
            @update:model-value="onUpdated()"
            :clearable="false"
            auto-apply
            :placeholder="t('search.departure')"
            model-type="yyyy-MM-dd"
            :min-date="dayjs(filters.startAvailabilityDate).add(2, 'day').format('YYYY-MM-DD')"
            locale="es"
            :enable-time-picker="false"
            :format="formatDate"
            position="center"
            :teleport="true"
          >
            <template #input-icon> </template>
          </VueDatePicker>
        </ion-col>

        <ion-col
          v-if="user?.userType === 'relocated' && organizationData"
          size="12"
          class="ion-no-padding my-sm"
        >
          <div class="explore-group-card">
            <div class="d-flex flex-column ion-justify-content-center">
              <p class="ion-justify-content-center w-100 font-md bold-medium">
                {{ organizationData.group.Project.ProjectLocation.fullAddress }}
              </p>
              <div class="d-flex ion-align-items-center gap-sm mt-sm">
                <ion-icon :icon="calendarOutline" color="grey" size="small" />
                <p class="font-sm">
                  {{ formatDateTime(organizationData.group.checkInDate, locale, true, true) }} -
                  {{ formatDateTime(organizationData.group.checkOutDate, locale, true, true) }}
                </p>
              </div>
            </div>
          </div>
        </ion-col>
        <ion-col size="12" class="ion-no-padding">
          <ion-list class="bg-white chips-list pb-sm">
            <ion-chip
              color="primary"
              :outline="true"
              class="form-control chips"
              :class="{ active: filters.PropertyTypeId === -1 }"
              @click="handleClearFilters()"
              >{{ t('filters.allProperties') }}</ion-chip
            >
            <ion-chip
              v-for="type in propertyTypeOptions"
              color="primary"
              :outline="true"
              class="form-control chips"
              :class="{ active: filters.PropertyTypeId === type.id }"
              :key="type.id"
              @click="setPropertyTypeFilter(type.id)"
            >
              <ion-label>{{ type[`name_${language}` as keyof typeof type] }}</ion-label>
            </ion-chip>
          </ion-list>
        </ion-col>
      </ion-row>
    </div>
  </ion-card>

  <ion-modal ref="modalFilter" trigger="open-modal" :initial-breakpoint="1" :breakpoints="[0, 1]">
    <div>
      <FiltersCardModalContainer :language="languageSelected" @close="onclose()" />
    </div>
  </ion-modal>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import VueDatePicker from '@vuepic/vue-datepicker';
  import {
    IonButton,
    IonCard,
    IonRow,
    IonModal,
    IonCol,
    IonSearchbar,
    IonIcon,
    IonList,
    IonLabel,
    IonChip,
  } from '@ionic/vue';
  import FiltersCardModalContainer from './FiltersCardModalContainer.vue';
  import { storeToRefs } from 'pinia';
  import { useI18n } from 'vue-i18n';
  import { PlaceLocation } from '@/interfaces/elastic/elastic.interface';
  import { useMapsStore } from '@/stores/maps-store';
  import dayjs from 'dayjs';
  import { format } from 'date-fns';
  import { useFilters } from '@/composables/useFilters';
  import { calendarOutline, settingsOutline } from 'ionicons/icons';
  import { useElasticStore } from '@/stores/elastic-store';
  import { useProfileStore } from '@/stores/profile-store';
  import { useAuth } from '@/composables/useAuth';
  import { formatDateTime } from '@/utils/date-utils';

  withDefaults(
    defineProps<{
      organizationData?: any;
    }>(),
    {},
  );

  const { selectedFilters, propertyTypeOptions, filters } = storeToRefs(useElasticStore());
  const { applyFilters } = useFilters();
  const { addOrClearFilter } = useElasticStore();
  const { languageSelected } = storeToRefs(useProfileStore());
  const { results } = storeToRefs(useMapsStore());
  const { searchDirection } = useMapsStore();
  const { t, locale } = useI18n();
  const language = languageSelected;
  const { getPropertyElements } = useElasticStore();
  const isLoading = ref(true);
  const { user } = useAuth();

  const modalFilter = ref();
  const onclose = () => modalFilter.value.$el.dismiss(null, 'cancel');
  const searchBar = ref('');

  const formatDate = (date: any) => {
    return format(new Date(date), 'd MMM, yyyy');
  };

  const onUpdated = () => {
    const startDate = dayjs(filters.value.startAvailabilityDate);
    const endDate = dayjs(filters.value.endAvailabilityDate);

    const isAfter = startDate.isAfter(endDate);

    if (isAfter) {
      filters.value.endAvailabilityDate = '';
    }

    applyFilters();
  };

  const setFilterLocation = (location?: PlaceLocation) => {
    filters.value.city = location && location.city ? location.city : '';
    filters.value.countryCode = location && location.countryCode ? location.countryCode : '';
    filters.value.country = location && location.country ? location.country : '';
    filters.value.lat = location && location.lat ? Number(location.lat) : undefined;
    filters.value.lon = location && location.lon ? Number(location.lon) : undefined;
    searchBar.value = location ? (location.fullAddress as string) : '';
    results.value = [];

    applyFilters();
  };

  const handleInput = (val: any) => {
    if (val.length > 2) {
      filters.value.city = '';
      searchDirection(val.toLowerCase());
    } else if (val === '') {
      setFilterLocation();
    }
  };

  const handleApplyFilters = () => {
    applyFilters();
    getPropertyElements();
  };

  const handleClearFilters = () => {
    addOrClearFilter('PropertyTypeId', false, false);
    filters.value.PropertyTypeId = -1;
    applyFilters();
    getPropertyElements();
  };

  const setPropertyTypeFilter = (typeId: number) => {
    filters.value.PropertyTypeId = typeId;
    handleApplyFilters();
  };

  const setInitialData = () => {
    filters.value.PropertyTypeId = selectedFilters.value.mappedFilters.PropertyTypeId
      ? Number(selectedFilters.value.mappedFilters.PropertyTypeId)
      : -1;
    isLoading.value = false;
  };

  onMounted(() => {
    if (filters.value.city) {
      searchBar.value = filters.value.city;
    }
    setInitialData();
    getPropertyElements();
  });
</script>

<style lang="css" scoped>
  .explore-group-card {
    border: 1px solid #c6c9d4;
    padding: var(--padding-xs);
    border-radius: 12px;
  }
  .filter-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--padding-sm);
  }

  .chips-list {
    display: flex;
    width: 100%;
    overflow-x: auto;
    padding: 8px 0;
    -webkit-overflow-scrolling: touch;
  }

  .chips-list .chips {
    flex-shrink: 0;
  }

  .img-decoration {
    max-width: 151px;
  }

  ion-modal {
    --height: auto;
  }

  .content-modal {
    padding: 24px 15px 150px 15px;
    height: 100%;
    overflow: auto;
  }

  .results {
    position: absolute;
    z-index: 99;
    width: 90%;
  }

  @media (max-width: 499px) {
    .filter-card {
      align-items: normal;
    }
  }
</style>
